import React, { Component } from 'react';

import { Activity, ArrowDownWideNarrow, ArrowUpNarrowWide, Medal, Users } from 'lucide-react';
import PropTypes from 'prop-types';

import DateRangePicker from 'common/common/DateRangePicker';
import DismissableTip from 'common/common/DismissableTip';
import { BoardsContext } from 'common/containers/BoardsContainer';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { OpenModalContext } from 'common/containers/ModalContainer';
import { LocationContext, RouterContext } from 'common/containers/RouterContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import ControlledDropdown from 'common/ControlledDropdown';
import CheckboxInput from 'common/inputs/CheckboxInput';
import AccessModal from 'common/modals/AccessModal';
import SavedSegmentsModal from 'common/modals/SavedSegmentsModal';
import UpsellModal from 'common/modals/UpsellModal';
import AdminPageSidebar from 'common/subdomain/admin/AdminPageSidebar';
import AdminSidebarSectionItem from 'common/subdomain/admin/AdminSidebarSectionItem';
import Tappable from 'common/Tappable';
import { Select } from 'common/uiV3/Select';
import { RelativeDateRanges } from 'common/util/dateRanges';
import hasPermission from 'common/util/hasPermission';
import trackLinkView from 'common/util/trackLinkView';
import withContexts from 'common/util/withContexts';

import AdminSidebarSection from './AdminSidebarSection';

import 'css/components/subdomain/admin/_AdminUsersSidebar.scss';

const Sorts = {
  relevance: {
    name: 'relevance',
    render: 'Relevance',
  },
  lastActivity: {
    name: 'lastActivity',
    render: 'Most recent activity',
  },
  oldestLastActivity: {
    name: 'oldestLastActivity',
    render: 'Least recent activity',
  },
  topPosters: {
    name: 'topPosters',
    render: 'Top Posters',
  },
  topVoters: {
    name: 'topVoters',
    render: 'Top Voters',
  },
};

const ActivityFilters = {
  posts: {
    render: 'Posts',
  },
  votes: {
    render: 'Votes',
  },
  comments: {
    render: 'Comments',
  },
};

function parseDateRange(dateRange) {
  if (!dateRange) {
    return RelativeDateRanges.allTime;
  }
  const dateRangeArr = dateRange.split('_');

  if (dateRangeArr.length === 1) {
    return dateRangeArr[0];
  }

  return dateRangeArr;
}

function serializeDateRange(dateRange) {
  if (!dateRange) {
    return undefined;
  }

  if (typeof dateRange === 'string') {
    if (dateRange === RelativeDateRanges.allTime) {
      return undefined;
    }
    return [dateRange];
  }

  return dateRange.join('_');
}

class AdminUsersSidebar extends Component {
  static propTypes = {
    company: PropTypes.shape({
      segments: PropTypes.array,
    }),
    location: PropTypes.object,
    openModal: PropTypes.func.isRequired,
    router: PropTypes.object,
    viewer: PropTypes.object,
  };

  state = {
    afterUpsell: null,
    segment: this.props.location.query.segment,
    showUpsellModal: false,
  };

  constructor(props, context) {
    super(props, context);

    this.activityRefs = {};
    Object.keys(ActivityFilters).forEach((activityName) => {
      this.activityRefs[activityName] = React.createRef();
    });
    this.segmentRef = React.createRef();
  }

  getSortFromQuery(query) {
    const isSearching = Boolean(query.search?.trim());

    if (isSearching) {
      return Sorts.relevance.name;
    }

    if (query.sort) {
      return query.sort;
    }

    return Sorts.lastActivity.name;
  }

  componentDidMount() {
    this.updateQuery();
  }

  changeSegment = (segment) => {
    this.setState({ segment }, this.updateQuery);
  };

  manageSegments = () => {
    const { boards, company, openModal, viewer } = this.props;
    const viewerHasPermission = hasPermission('manageUserSegmentation', company, viewer);
    if (!viewerHasPermission) {
      openModal(AccessModal, {
        requiredPermissions: ['manageUserSegmentation'],
      });
      return;
    }

    openModal(SavedSegmentsModal, {
      boards,
      onUpdateQuery: this.updateQuery,
    });
  };

  updateQuery = (updates = {}) => {
    const {
      company: { segments },
      location,
      router,
    } = this.props;
    const { segment } = this.state;
    const segmentObject = segments.find(({ urlName }) => urlName === segment);

    const filters = [];
    const allFilters = Object.keys(ActivityFilters);
    allFilters.forEach((name) => {
      const ref = this.activityRefs[name].current;
      if (ref && ref.getValue()) {
        filters.push(name);
      }
    });

    // set the default sort to `undefined` when searching
    const isSearching = Boolean(location.query.search?.trim());
    let newSort = updates.sort ?? location.query.sort;
    if (newSort === Sorts.lastActivity.name) {
      newSort = undefined;
    }

    router.replace({
      ...location,
      query: {
        ...location.query,
        ...updates,
        filter: (filters.length < allFilters.length && filters.join('-')) || undefined,
        segment: (segmentObject && segment) || undefined,
        sort: isSearching ? undefined : newSort,
      },
    });
  };

  onManageSegments = async () => {
    const { features } = this.props.company;
    const planSupports = features?.userSegmentation;
    if (!planSupports) {
      this.setState({
        afterUpsell: this.manageSegments,
        showUpsellModal: true,
      });
      return;
    }

    this.manageSegments();
  };

  onSegmentChange = async (segment) => {
    const { features } = this.props.company;
    const planSupports = features?.userSegmentation;
    if (!planSupports) {
      this.setState({
        afterUpsell: () => this.changeSegment(segment),
        showUpsellModal: true,
      });
      return;
    }

    this.changeSegment(segment);
  };

  onDateRangeSelected = (e) => {
    const newDateRange = e.relativeDate ?? e.dateRange;

    this.updateQuery({
      dateRange: serializeDateRange(newDateRange),
    });
  };

  onSortChange = (sort) => {
    this.updateQuery({ sort });
  };

  onActivityChange = () => {
    this.updateQuery();
  };

  onUpsell = () => {
    const { afterUpsell } = this.state;
    this.setState({
      afterUpsell: null,
      showUpsellModal: false,
    });
    afterUpsell();
  };

  onUpsellDismiss = () => {
    this.setState({
      afterUpsell: null,
      showUpsellModal: false,
    });
  };

  renderSegmentSection() {
    const {
      company: { segments },
    } = this.props;
    const segmentOptions = [
      { name: 'everyone', render: 'Everyone (default)' },
      ...segments.map(({ name, urlName }) => ({ name: urlName, render: name })),
    ];
    const segment = segments.find(({ urlName }) => urlName === this.state.segment);
    return (
      <AdminSidebarSection
        action={
          <Tappable onTap={this.onManageSegments}>
            <div className="icon icon-gear" />
          </Tappable>
        }
        className="segmentSection"
        title="User Segment">
        <ControlledDropdown
          className="segmentDropdown"
          dropdownClassName="adminBoardSidebarSegmentDropdown"
          ref={this.segmentRef}
          onChange={this.onSegmentChange}
          options={segmentOptions}
          selectedName={(segment && segment.urlName) || 'everyone'}
        />
      </AdminSidebarSection>
    );
  }

  renderSorts() {
    const {
      location: { query },
    } = this.props;
    const isSearching = Boolean(query.search?.trim());
    const sort = this.getSortFromQuery(query);
    const options = isSearching
      ? [
          {
            label: (
              <Select.OptionLabel>
                <Activity />
                <p>Relevance</p>
              </Select.OptionLabel>
            ),
            value: Sorts.relevance.name,
          },
        ]
      : [
          {
            label: (
              <Select.OptionLabel>
                <ArrowDownWideNarrow />
                <p className="truncate">{Sorts.lastActivity.render}</p>
              </Select.OptionLabel>
            ),
            value: Sorts.lastActivity.name,
          },
          {
            label: (
              <Select.OptionLabel>
                <ArrowUpNarrowWide />
                <p className="truncate">{Sorts.oldestLastActivity.render}</p>
              </Select.OptionLabel>
            ),
            value: Sorts.oldestLastActivity.name,
          },
          {
            label: (
              <Select.OptionLabel>
                <Medal />
                <p>{Sorts.topPosters.render}</p>
              </Select.OptionLabel>
            ),
            value: Sorts.topPosters.name,
          },
          {
            label: (
              <Select.OptionLabel>
                <Users />
                <p>{Sorts.topVoters.render}</p>
              </Select.OptionLabel>
            ),
            value: Sorts.topVoters.name,
          },
        ];

    return (
      <AdminSidebarSection title="Sort">
        <Select options={options} value={sort} onValueChange={this.onSortChange} />
      </AdminSidebarSection>
    );
  }

  renderActivityFilter() {
    const {
      location: { query },
    } = this.props;
    return (
      <AdminSidebarSection title="Activity">
        {Object.keys(ActivityFilters).map((name) => {
          const isChecked = query.filter ? query.filter.includes(name) : true;
          return (
            <AdminSidebarSectionItem key={name}>
              <CheckboxInput
                defaultChecked={isChecked}
                label={ActivityFilters[name].render}
                onChange={this.onActivityChange}
                ref={this.activityRefs[name]}
              />
            </AdminSidebarSectionItem>
          );
        })}
      </AdminSidebarSection>
    );
  }

  renderDateRangeFilter() {
    const {
      location: { query },
    } = this.props;
    const dateRange = parseDateRange(query.dateRange);

    return (
      <AdminSidebarSection title="Date Range">
        <DateRangePicker align="start" date={dateRange} onSubmit={this.onDateRangeSelected} />
      </AdminSidebarSection>
    );
  }

  render() {
    const { location } = this.props;

    return (
      <div className="adminUsersSidebar">
        <AdminPageSidebar showSidebar={true}>
          {this.renderSegmentSection()}
          {this.renderDateRangeFilter()}
          {this.renderSorts()}
          {this.renderActivityFilter()}
          <DismissableTip
            className="mx-5"
            name="identifyUsersTip"
            title="Get the customer&nbsp;context"
            subtitle="Use Canny Identify to connect profiles with company, plan, and revenue data."
            buttonText="Learn how"
            buttonHref="https://help.canny.io/en/articles/1626097-canny-identify"
            onButtonClick={() => {
              trackLinkView('clicked_identify_users_tip', location.pathname, location.search);
            }}
          />
          <UpsellModal
            cta="Filter feedback to user groups you care&nbsp;about"
            feature="userSegmentation"
            onClose={this.onUpsellDismiss}
            onUpsell={this.onUpsell}
            show={this.state.showUpsellModal}
          />
        </AdminPageSidebar>
      </div>
    );
  }
}

export default withContexts({
  boards: BoardsContext,
  company: CompanyContext,
  location: LocationContext,
  openModal: OpenModalContext,
  router: RouterContext,
  viewer: ViewerContext,
})(AdminUsersSidebar);
